import { css, SerializedStyles } from '@emotion/core';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  Theme,
  normalText,
  secondaryText,
  helpText,
  shadowFrameSelected,
} from 'features/Theme';

import {
  Attributes,
  ComponentClass,
  createElement,
  FunctionComponent,
} from 'react';

import { FaCheck } from 'react-icons/fa';

const reactSvgComponentToMarkupString = <Props extends {}>(
  Component: string | FunctionComponent<{}> | ComponentClass<{}, unknown>,
  props?: Attributes & Props | null | undefined,
) => {
  // https://stackoverflow.com/questions/56621708/how-to-get-material-ui-icon-svg-path-react-icon-picker
  return `data:image/svg+xml,${encodeURIComponent(
    renderToStaticMarkup(createElement(Component, props)),
  )}`;
};

const checkmarkSvg = (color: string) =>
  reactSvgComponentToMarkupString(FaCheck, {
    fill: color,
  });

const styles = {
  titleText: (theme: Theme) => css`
    margin-bottom: ${theme.mui.spacing(3)}px;
  `,
  normalText: (theme: Theme) => css`
    ${normalText(theme)};
    padding-bottom: ${theme.mui.spacing(1)}px;
  `,
  headingContainer: css`
    text-align: center;
  `,

  offerDetailsDisclosureButton: (theme: Theme): SerializedStyles => css`
    button {
      ${secondaryText(theme)}
      padding: 0;
      cursor: pointer;
    }
    font-size: inherit;
    color: ${theme.mui.palette.primary.main};
    background: none;
    border: none;
  `,

  offerDetailText: (theme: Theme): SerializedStyles => css`
    &,
    p {
      ${helpText(theme)}
    }
  `,
  radioGroup: css`
    flex: 1;
  `,

  cardPickerContainer: (theme: Theme): SerializedStyles => css`
    margin: ${theme.mui.spacing(2)}px 0;
  `,

  cardPickerRow: css`
    overflow: hidden;
  `,

  cardOptionLabel: (theme: Theme): SerializedStyles => css`
    ${theme.mui.typography.body1}
    margin-top: ${theme.mui.spacing(1)}px;
    text-align: center;
    font-weight: bold;
  `,

  cardLabelColumn: (theme: Theme): SerializedStyles => css`
    word-wrap: break-word;
    max-width: 200px;
  `,
  cardOptionSubLabel: (theme: Theme): SerializedStyles => css`
    ${theme.mui.typography.body2}
    text-align: left;
    padding-top: ${theme.mui.spacing(1)}px;
    padding-bottom: ${theme.mui.spacing(1)}px;
    word-wrap: break-word;
  `,

  cardImageContainer: (theme: Theme): SerializedStyles => css`
    width: 100%;
    height: auto;
    overflow: hidden;
    margin-top: 0.6em;
  `,

  cardOptionImage: css`
    width: 100%;
    height: auto;
    max-width: 180px;
  `,

  cardOptionCardSelected: (theme: Theme): SerializedStyles => css`
    transition: ${theme.mui.transitions.create('box-shadow', {
      duration: theme.mui.transitions.duration.shortest,
    })};
    ${shadowFrameSelected()};
  `,

  benefitBody: (theme: Theme): SerializedStyles => css`
    ${theme.mui.typography.body2}
    text-align: left;
  `,

  radioInput: (theme: Theme): SerializedStyles => css`
    &.MuiFormControlLabel-root {
      display: flex;
      align-items: flex-start;
      ${theme.mui.breakpoints.down('sm')} {
      justify-content: center;
    }

  `,

  benefitsList: (theme: Theme) => css`
    ul {
      list-style-type: none;
      padding-inline-start: 0;
    }

    li {
      padding-left: ${theme.mui.spacing(3)}px;
      position: relative;
    }

    li + li {
      margin-top: ${theme.mui.spacing(1)}px;
      p {
        ${theme.mui.typography.body2}
        margin-top: 0;
      }
    }

    li::before {
      content: url(${checkmarkSvg(theme.mui.palette.success.main)});
      left: 0;
      position: absolute;
      // Would prefer to center this over the line height, but can't seem
      // to be able to access this value anywhere.
      top: 2px;
    }

    hr {
      border-color: ${theme.mui.palette.divider};
      border-width: 1px;
      border-top: none;
    }
  `,

  expandableDetailLabel: (theme: Theme) => css`
    span {
      ${theme.mui.typography.body2}
      font-weight: normal;
    }
  `,

  expandableBenefitsLabel: (theme: Theme) => css`
    span {
      ${theme.mui.typography.body2}
      font-weight: bold;
    }
  `,
  bottomDisclosure: (theme: Theme) => css`
    ${theme.mui.typography.body2}
    margin: ${theme.mui.spacing(2)}px 0;
    text-align: center;
  `,
  bottomDisclosureFinePrint: (theme: Theme) => css`
    text-align: start;
    ${helpText(theme)};
    p + p {
      margin-top: ${theme.mui.spacing(1)}px;
    }
  `,

  stickyFooter: (theme: Theme) => css`
    padding-top: ${theme.mui.spacing(3)}px;
    padding-bottom: ${theme.mui.spacing(2)}px;
  `,
};

export default styles;
